import { useState, useEffect } from "react";
import { database, auth } from "../firebase";
import { ref, onValue, off } from "firebase/database";
import { onAuthStateChanged } from "firebase/auth";
import ReactDOMServer from "react-dom/server";
import * as XLSX from "xlsx";


const parseSaleDate = (dateString) => {
  if (!dateString) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const parts = dateString.split("-");
    return new Date(Number.parseInt(parts[0], 10), Number.parseInt(parts[1], 10) - 1, Number.parseInt(parts[2], 10));
  }
  if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(dateString)) {
    const parts = dateString.split(/[-/]/);
    return new Date(Number.parseInt(parts[2], 10), Number.parseInt(parts[1], 10) - 1, Number.parseInt(parts[0], 10));
  }
  return new Date(dateString);
}

const getCityFromAddress = (address) => {
  if (!address) return "Unknown City"
  const addressParts = address.trim().split(/\s+/)
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : "Unknown City"
}

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) {
      // "All" option
      if (selected.includes(option)) {
        onChange([])
      } else {
        onChange([...options])
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter((item) => item !== option && item !== options[0])
        : [...selected.filter((item) => item !== options[0]), option]
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected])
      } else {
        onChange(newSelected)
      }
    }
  }

  return (
    <div
      style={{
        backgroundColor: "#f8fafc",
        padding: "16px",
        borderRadius: "8px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
        transition: "all 0.3s ease",
        maxWidth: "220px",
        marginBottom: "16px",
      }}
    >
      <h2
        style={{
          fontSize: "16px",
          fontWeight: "600",
          color: "#1e293b",
          marginBottom: "8px",
          borderBottom: "2px solid #3b82f6",
          paddingBottom: "6px",
        }}
      >
        {title}
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {options.map((option) => (
          <label
            key={option}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              padding: "6px",
              borderRadius: "6px",
              transition: "background-color 0.2s",
              backgroundColor: selected.includes(option) ? "#eff6ff" : "transparent",
            }}
            onMouseEnter={(e) => {
              if (!selected.includes(option)) {
                e.currentTarget.style.backgroundColor = "#f1f5f9"
              }
            }}
            onMouseLeave={(e) => {
              if (!selected.includes(option)) {
                e.currentTarget.style.backgroundColor = "transparent"
              }
            }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: "14px",
                width: "14px",
                accentColor: "#3b82f6",
                cursor: "pointer",
              }}
            />
            <span
              style={{
                fontSize: "14px",
                color: "#475569",
                userSelect: "none",
                fontWeight: selected.includes(option) ? "500" : "400",
              }}
            >
              {option}
            </span>
          </label>
        ))}
      </div>
    </div>
  )
}

// Sales Report GSTIN Display Component
const SalesReportGSTINDisplay = ({ formData, filters, branchProfile, salesData }) => {
  // Helper function to format date for display
  const formatDisplayDate = (dateString) => {
    const date = parseSaleDate(dateString);
    return !date || isNaN(date.getTime())
      ? "N/A"
      : date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
  };

  // Filter salesData by selected criteria
  let filteredSalesData = salesData;
  console.log('Total sales data before filtering:', salesData.length);

  // Apply date filter
  if (formData.fromDate && formData.toDate) {
    const fromDate = new Date(formData.fromDate);
    const toDate = new Date(formData.toDate);
    toDate.setHours(23, 59, 59, 999);
    filteredSalesData = filteredSalesData.filter((item) => {
      const itemDate = parseSaleDate(item.date);
      return itemDate && !isNaN(itemDate.getTime()) && itemDate >= fromDate && itemDate <= toDate;
    });
  }

  // Apply store filter
  if (!filters.store.includes("All Stores")) {
    filteredSalesData = filteredSalesData.filter((item) => filters.store.includes(item.store));
  }

  // Apply customer filter
  if (!filters.customer.includes("All Customers")) {
    filteredSalesData = filteredSalesData.filter((item) => filters.customer.includes(item.customer));
  }

  // Apply group filter
  if (!filters.group.includes("All Group Names")) {
    filteredSalesData = filteredSalesData.filter((item) => filters.group.includes(item.groupName));
  }

  // Apply product filter
  if (!filters.product.includes("All Products")) {
    filteredSalesData = filteredSalesData.filter((item) => filters.product.includes(item.itemDescription));
  }

  console.log('Filtered sales data:', filteredSalesData.length, 'records');

  // Process data for GSTIN report format
  let displayData = [];
  if (formData.reportType === "abstract") {
    // Group by customer for abstract report
    const groupedData = filteredSalesData.reduce((acc, item) => {
      const key = item.customer;
      if (!acc[key]) {
        acc[key] = {
          customer: item.customer,
          customerPhone: item.customerPhone || "N/A",
          customerGSTIN: item.customerGSTIN || "N/A",
          srnNo: item.billNo || "N/A",
          srnDate: item.date || "N/A",
          qty: 0,
          taxAmount0: 0,
          taxableValue0: 0,
          taxAmount28: 0,
          taxableValue28: 0,
          taxAmount18: 0,
          taxableValue18: 0,
          cgstAmt: 0,
          sgstAmt: 0,
          igstAmt: 0,
          cessAmt: 0,
          totalTax: 0,
          discAmount: 0,
          grossAmount: 0, // Will be sum of unitRate * qty
          billDiscAmount: 0,
          netValue: 0,
          count: 0,
        };
      }
      
      // Use the corrected data from the sales record
      const qty = Number.parseFloat(item.qty) || 0;
      const taxableValue = Number.parseFloat(item.netValue) || 0;
      const discount = Number.parseFloat(item.discount) || 0;
      const taxAmount = Number.parseFloat(item.taxAmount) || 0;
      const netAmount = Number.parseFloat(item.netAmount) || 0;
      const cgstAmt = Number.parseFloat(item.cgstAmt) || 0;
      const sgstAmt = Number.parseFloat(item.sgstAmt) || 0;
      const igstAmt = Number.parseFloat(item.igstAmt) || 0;
      const cessAmt = Number.parseFloat(item.cessAmount) || 0;
      const totalTax = cgstAmt + sgstAmt + igstAmt + cessAmt;

      // Correct grossAmount: only unitRate * qty (no tax, no discount)
      const unitRate = Number.parseFloat(item.unitRate) || 0;
      const grossAmount = unitRate * qty;

      acc[key].qty += qty;
      acc[key].taxAmount0 += item.tax === 0 ? taxAmount : 0;
      acc[key].taxableValue0 += item.tax === 0 ? taxableValue : 0;
      acc[key].taxAmount28 += item.tax === 28 ? taxAmount : 0;
      acc[key].taxableValue28 += item.tax === 28 ? taxableValue : 0;
      acc[key].taxAmount18 += item.tax === 18 ? taxAmount : 0;
      acc[key].taxableValue18 += item.tax === 18 ? taxableValue : 0;
      acc[key].cgstAmt += cgstAmt;
      acc[key].sgstAmt += sgstAmt;
      acc[key].igstAmt += igstAmt;
      acc[key].cessAmt += cessAmt;
      acc[key].totalTax += totalTax;
      acc[key].discAmount += discount;
      acc[key].grossAmount += grossAmount; // Only unitRate * qty
      acc[key].billDiscAmount += 0; // Assuming no bill-level discount
      acc[key].netValue += netAmount;
      acc[key].count += 1;
      return acc;
    }, {});
    displayData = Object.values(groupedData);
    displayData.sort((a, b) => a.customer.localeCompare(b.customer));
  } else {
    // Detail report - transform sales data to GSTIN format with all columns from the image
    displayData = filteredSalesData.map((item) => {
      const qty = Number.parseFloat(item.qty) || 0;
      const taxableValue = Number.parseFloat(item.netValue) || 0;
      const discount = Number.parseFloat(item.discount) || 0;
      const taxAmount = Number.parseFloat(item.taxAmount) || 0;
      const netAmount = Number.parseFloat(item.netAmount) || 0;
      const cgstAmt = Number.parseFloat(item.cgstAmt) || 0;
      const sgstAmt = Number.parseFloat(item.sgstAmt) || 0;
      const igstAmt = Number.parseFloat(item.igstAmt) || 0;
      const cessAmt = Number.parseFloat(item.cessAmount) || 0;
      const totalTax = cgstAmt + sgstAmt + igstAmt + cessAmt;

      // Only unitRate * qty for grossAmount
      const unitRate = Number.parseFloat(item.unitRate) || 0;
      const grossAmount = unitRate * qty;

      return {
        invoiceId: item.saleId,
        invoiceNo: item.billNo || "N/A",
        invoiceDate: item.date,
        store: item.store,
        customer: item.customer,
        customerPhone: item.customerPhone || "N/A",
        customerGSTIN: item.customerGSTIN || "N/A",
        tin: item.tin || "N/A",
        invNo: item.invNo || "N/A",
        invDate: item.invDate || item.date || "N/A",
        productName: item.itemDescription,
        groupName: item.groupName,
        hsnCode: item.hsnCode || "N/A",
        qty: qty,
        taxableValue: taxableValue,
        cgstAmt: cgstAmt,
        sgstAmt: sgstAmt,
        igstAmt: igstAmt,
        cessAmt: cessAmt,
        totalTax: totalTax,
        netValue: netAmount,
        cgstPerc: item.cgstPerc || 0,
        sgstPerc: item.sgstPerc || 0,
        igstPerc: item.igstPerc || 0,
        cessPerc: item.cessPerc || 0,
        discAmount: discount,
        discPerc: item.discPercent || 0,
        grossAmount: grossAmount, // Only unitRate * qty
        // Additional fields for better reporting
        salesMan: item.salesMan || "Unknown Salesman",
        terms: item.terms || "CASH",
        remarks: item.remarks || "",
        otherDetails: item.otherDetails || "",
        status: item.status || "completed",
        createdAt: item.createdAt || "",
      };
    });
    displayData.sort((a, b) => {
      return a.customer.localeCompare(b.customer) || parseSaleDate(a.invoiceDate) - parseSaleDate(b.invoiceDate);
    });
  }

  // Calculate totals
  const totalQty = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.qty) || 0), 0);
  const totalTaxableValue = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.taxableValue) || 0), 0);
  const totalCgstAmt = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.cgstAmt) || 0), 0);
  const totalSgstAmt = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.sgstAmt) || 0), 0);
  const totalIgstAmt = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.igstAmt) || 0), 0);
  const totalCessAmt = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.cessAmt) || 0), 0);
  const totalTax = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.totalTax) || 0), 0);
  const totalNetValue = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.netValue) || 0), 0);
  const totalDiscAmount = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.discAmount) || 0), 0);
  const totalGrossAmount = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.grossAmount) || 0), 0);

  const city = branchProfile.city || (branchProfile.address ? branchProfile.address.split(/\s+/).pop() : 'Unknown City');

  const renderTableHeaders = () => {
    if (formData.reportType === "abstract") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Customer Name</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Phone Number</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GSTIN</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Inv.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Inv.Date</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Tax Amount (0.00%)</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Taxable Value (0.00%)</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Tax Amount (28.00%)</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Taxable Value (28.00%)</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Tax Amount (18.00%)</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Taxable Value (18.00%)</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Cess (CESS 0%)</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Item Disc Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Gross Amount</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Bill Disc Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>NetAmount</th>
        </tr>
      );
    } else {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Customer Name</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Phone Number</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>TIN</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Inv.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Inv.Date</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CGST %</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>SGST %</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>IGST %</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CESS<br />%</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc<br />%</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Gross Amount</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Amount</th>
        </tr>
      );
    }
  };

const renderTableRows = () => {
  if (displayData.length === 0) {
    return (
      <tr>
        <td
          colSpan={formData.reportType === "abstract" ? 19 : 14}
          style={{
            border: '1px solid #cbd5e1',
            padding: '30px',
            textAlign: 'center',
            color: '#64748b',
            fontStyle: 'italic',
            fontSize: '14px',
            backgroundColor: '#f8fafc'
          }}
        >
          No sales data found for the selected criteria
        </td>
      </tr>
    );
  }
  return displayData.map((item, index) => {
    if (formData.reportType === "abstract") {
      return (
        <tr key={item.customer} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.customer}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.customerPhone || "N/A"}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{formatDisplayDate(item.srnDate)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.taxAmount0.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.taxableValue0.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.taxAmount28.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.taxableValue28.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.taxAmount18.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.taxableValue18.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.totalTax.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.cessAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.discAmount.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.grossAmount.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.totalTax.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.billDiscAmount.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.netValue.toFixed(2)}</td>
        </tr>
      );
    } else {
      
      // Only unitRate * qty for gross amount
      const grossAmount = item.grossAmount;
      // Net amount is taxable value (after discount, before taxes)
      const netAmount = item.taxableValue + item.cgstAmt + item.sgstAmt + item.igstAmt + item.cessAmt;;
      return (
        <tr
          key={`${item.invoiceId}-${item.productName}-${index}`}
          style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}
        >
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.customer}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.customerPhone}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.tin}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.invNo}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{formatDisplayDate(item.invDate)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
            {item.cgstPerc > 0 ? `${item.cgstPerc.toFixed(2)}%` : "-"}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
            {item.sgstPerc > 0 ? `${item.sgstPerc.toFixed(2)}%` : "-"}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
            {item.igstPerc > 0 ? `${item.igstPerc.toFixed(2)}%` : "-"}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.totalTax.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
            {item.cessPerc > 0 ? `${item.cessPerc.toFixed(2)}%` : "-"}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
            {item.discPerc > 0 ? `${item.discPerc.toFixed(2)}%` : "-"}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{grossAmount.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{netAmount.toFixed(2)}</td>
        </tr>
      );
    }
  });
};

const renderTotalRow = () => {
  if (displayData.length === 0) return null;
  if (formData.reportType === "abstract") {
    const totalTaxAmount0 = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.taxAmount0) || 0), 0);
    const totalTaxableValue0 = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.taxableValue0) || 0), 0);
    const totalTaxAmount28 = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.taxAmount28) || 0), 0);
    const totalTaxableValue28 = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.taxableValue28) || 0), 0);
    const totalTaxAmount18 = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.taxAmount18) || 0), 0);
    const totalTaxableValue18 = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.taxableValue18) || 0), 0);
    const totalTax = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.totalTax) || 0), 0);
    const totalCessAmt = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.cessAmt) || 0), 0);
    const totalDiscAmount = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.discAmount) || 0), 0);
    const totalGrossAmount = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.grossAmount) || 0), 0);
    const totalBillDiscAmount = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.billDiscAmount) || 0), 0);
    const totalNetValue = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.netValue) || 0), 0);

    return (
      <>
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="6" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalTaxAmount0.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalTaxableValue0.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalTaxAmount28.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalTaxableValue28.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalTaxAmount18.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalTaxableValue18.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalTax.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalCessAmt.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalDiscAmount.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalGrossAmount.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalTax.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalBillDiscAmount.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalNetValue.toFixed(2)}
          </td>
        </tr>
        <tr style={{ backgroundColor: '#fee2e2', fontWeight: 'bold', borderTop: '2px solid #dc2626' }}>
          <td colSpan="6" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Grand Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalTaxAmount0.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalTaxableValue0.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalTaxAmount28.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalTaxableValue28.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalTaxAmount18.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalTaxableValue18.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalTax.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalCessAmt.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalDiscAmount.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalGrossAmount.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalTax.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalBillDiscAmount.toFixed(2)}
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>
            {totalNetValue.toFixed(2)}
          </td>
        </tr>
      </>
    );
  } else {
    // Only unitRate * qty for gross amount
    return (
      <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
        <td colSpan="6" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
          Total
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>-</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>-</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>-</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalTax.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>-</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>-</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGrossAmount.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalNetValue.toFixed(2)}</td>
      </tr>
    );
  }
};

  return (
    <div
      style={{
        backgroundColor: 'white',
        padding: '30px',
        marginTop: '20px',
        border: '2px solid #3b82f6',
        borderRadius: '12px',
        fontFamily: 'Arial, sans-serif',
        fontSize: '12px',
        pageBreakInside: 'avoid',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
      }}
      className="report-display"
    >
      {/* Header */}
      <div
        style={{
          textAlign: 'center',
          marginBottom: '30px',
          paddingBottom: '20px',
          borderBottom: '2px solid #e2e8f0'
        }}
      >
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 'bold',
            margin: '0',
            marginBottom: '5px',
            color: '#1e293b',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
          }}
        >
          {branchProfile.branchName}
        </h1>
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#3b82f6', marginBottom: '10px' }}>{city}</div>
        <div style={{ fontSize: '14px', color: '#475569', marginBottom: '10px' }}>
          Customerwise Sales Annexure Report From : 
          {formData.fromDate ? new Date(formData.fromDate).toLocaleDateString('en-GB') : '01-06-2025'} To : 
          {formData.toDate ? new Date(formData.toDate).toLocaleDateString('en-GB') : '30-06-2025'}
        </div>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
          Option: {formData.reportType === 'detail' ? 'Detail' : 'Abstract'}
        </div>
      </div>

      {/* Sales Table */}
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '11px',
            border: '2px solid #3b82f6',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
        >
          <thead>{renderTableHeaders()}</thead>
          <tbody>
            {renderTableRows()}
            {renderTotalRow()}
          </tbody>
        </table>
      </div>

      {/* Summary Section */}
      {displayData.length > 0 && (
        <div
          style={{
            marginTop: '30px',
            padding: '20px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}
        >
          <h3
            style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '15px',
              color: '#1e293b'
            }}
          >
            Summary
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '15px'
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Records</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>{displayData.length}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Quantity</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>{totalQty.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total CGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>{totalCgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total SGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#0891b2' }}>{totalSgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total IGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#c2410c' }}>{totalIgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total CESS</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>{totalCessAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Gross Amount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>{totalGrossAmount.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Net Amount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>{totalNetValue.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Component
const SalesReportGSTIN_1A = () => {
  const today = new Date()
  const [fromDate, setFromDate] = useState(today.toISOString().split("T")[0])
  const [toDate, setToDate] = useState(today.toISOString().split("T")[0])
  const [searchBy, setSearchBy] = useState("Customerwise")
  const [reportType, setReportType] = useState("detail")
  const [filters, setFilters] = useState({
    store: ["All Stores"],
    customer: ["All Customers"],
    group: ["All Group Names"],
    product: ["All Products"],
  })
  const [stores, setStores] = useState(["All Stores"])
  const [customers, setCustomers] = useState(["All Customers"])
  const [groups, setGroups] = useState(["All Group Names"])
  const [products, setProducts] = useState(["All Products"])
  const [currentUser, setCurrentUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showReport, setShowReport] = useState(false)
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: "",
  })
  const [salesData, setSalesData] = useState([])
  const [productGroupMap, setProductGroupMap] = useState({})
  const [productCategoryMap, setProductCategoryMap] = useState({})
  const [productBrandMap, setProductBrandMap] = useState({})
  const [customerMap, setCustomerMap] = useState({})

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, "_")
  }

  // Firebase Auth and Data Loading
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user)
      if (user) {
        loadFirebaseData(user)
      }
      setLoading(false)
    })
    return () => unsubscribe()
  }, [])

  const loadFirebaseData = (user) => {
    const encodedEmail = encodeEmailForPath(user.email)
    // Load Branch Profile
    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`)
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val()
      if (data) {
        const profiles = Object.values(data)
        const latestProfile = profiles[profiles.length - 1]
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ""} ${latestProfile.address2 || ""}`.trim(),
          phone: latestProfile.mobileNo || "",
          city:
            latestProfile.city || getCityFromAddress(`${latestProfile.address1 || ""} ${latestProfile.address2 || ""}`),
        })
      }
    })

    // Load Stores
    const storesRef = ref(database, `users/${encodedEmail}/stores`)
    onValue(storesRef, (snapshot) => {
      const storesData = ["All Stores"]
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const store = childSnapshot.val()
          if (store.name && !storesData.includes(store.name)) {
            storesData.push(store.name)
          }
        })
      }
      setStores(storesData)
    })

    // Load Customers
    const customersRef = ref(database, `users/${encodedEmail}/customers`)
    onValue(customersRef, (snapshot) => {
      const customersData = ["All Customers"]
      const customerMapObj = {}
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const customer = childSnapshot.val()
          if (customer.customerName && !customersData.includes(customer.customerName)) {
            customersData.push(customer.customerName)
          }
          if (customer.customerName) {
            customerMapObj[customer.customerName] = customer
          }
        })
      }
      setCustomers(customersData)
      setCustomerMap(customerMapObj)
    })

    // Load Groups
    const groupsRef = ref(database, `users/${encodedEmail}/productGroups`)
    onValue(groupsRef, (snapshot) => {
      const groupsData = ["All Group Names"]
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const group = childSnapshot.val()
          if (group.name && !groupsData.includes(group.name)) {
            groupsData.push(group.name)
          }
        })
      }
      setGroups(groupsData)
    })

    // Load Products and their mappings
    const productsRef = ref(database, `users/${encodedEmail}/products`)
    onValue(productsRef, (snapshot) => {
      const productsData = ["All Products"]
      const groupMap = {}
      const categoryMap = {}
      const brandMap = {}
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const product = childSnapshot.val()
          if (product.productName && !productsData.includes(product.productName)) {
            productsData.push(product.productName)
            groupMap[product.productName] = product.groupName || product.group || "No Group Assigned"
            categoryMap[product.productName] = product.category || "Unknown Category"
            brandMap[product.productName] = product.brand || "Unknown Brand"
          }
        })
      }
      setProducts(productsData)
      setProductGroupMap(groupMap)
      setProductCategoryMap(categoryMap)
      setProductBrandMap(brandMap)
    })

    // Load Sales Data (same structure as SalesReport)
    const salesRef = ref(database, `users/${encodedEmail}/sales`)
    onValue(salesRef, (snapshot) => {
      const data = snapshot.val()
      if (data) {
        const salesRecords = []
        Object.entries(data).forEach(([saleId, saleData]) => {
          if (saleData.items && Array.isArray(saleData.items)) {
            saleData.items.forEach((item) => {
              if (item.itemDescription) {
                // Get customer info from the map
                const customerInfo = customerMap[saleData.customer] || {}
                const phoneNo = customerInfo.phoneNo || saleData.phone || "N/A"
                const gstTinNo = customerInfo.gstTinNo || saleData.gstin || "N/A"
                
                // Parse numeric values
                const qty = Number.parseFloat(item.qty) || 0
                const unitRate = Number.parseFloat(item.unitRate) || 0
                const discPercent = Number.parseFloat(item.discPercent) || 0
                const tax = Number.parseFloat(item.tax) || 0
                
                // Calculate amounts correctly
                const baseAmount = qty * unitRate
                const discount = (baseAmount * discPercent) / 100
                const taxableValue = baseAmount - discount
                const taxAmount = (taxableValue * tax) / 100
                const netValue = taxableValue
                const netAmount = netValue + taxAmount
                
                // Calculate CGST/SGST/IGST based on tax percentage
                const cgstPerc = item.cgstPerc || (tax / 2)
                const sgstPerc = item.sgstPerc || (tax / 2)
                const igstPerc = item.igstPerc || 0
                const cessPerc = item.cessPerc || 0
                
                const cgstAmt = (taxableValue * cgstPerc) / 100
                const sgstAmt = (taxableValue * sgstPerc) / 100
                const igstAmt = (taxableValue * igstPerc) / 100
                const cessAmt = (taxableValue * cessPerc) / 100
                
                salesRecords.push({
                  saleId: saleId,
                  billNo: saleData.billNo || "N/A",
                  date: saleData.date || "",
                  store: saleData.store || "Unknown Store",
                  customer: saleData.customer || "Unknown Customer",
                  customerGSTIN: saleData.customerGSTIN || gstTinNo,
                  customerPhone: phoneNo,
                  tin: gstTinNo,
                  invNo: saleData.invNo || saleData.billNo || "N/A",
                  invDate: saleData.invDate || saleData.date || "N/A",
                  itemDescription: item.itemDescription || "Unknown Product",
                  groupName: productGroupMap[item.itemDescription] || "No Group Assigned",
                  category: productCategoryMap[item.itemDescription] || "Unknown Category",
                  brand: productBrandMap[item.itemDescription] || "Unknown Brand",
                  unit: item.unit || "",
                  qty: qty,
                  unitRate: unitRate,
                  discount: discount,
                  taxAmount: taxAmount,
                  cessAmount: cessAmt,
                  netValue: netValue,
                  netAmount: netAmount,
                  hsnCode: item.hsnCode || "N/A",
                  tax: tax,
                  cgstPerc: cgstPerc,
                  sgstPerc: sgstPerc,
                  igstPerc: igstPerc,
                  cessPerc: cessPerc,
                  cgstAmt: cgstAmt,
                  sgstAmt: sgstAmt,
                  igstAmt: igstAmt,
                  discPercent: discPercent,
                  // Additional fields for better reporting
                  salesMan: saleData.salesMan || "Unknown Salesman",
                  terms: saleData.terms || "CASH",
                  remarks: saleData.remarks || "",
                  otherDetails: saleData.otherDetails || "",
                  status: saleData.status || "completed",
                  createdAt: saleData.createdAt || "",
                })
              }
            })
          }
        })
        console.log('Loaded sales records:', salesRecords.length, 'records');
        console.log('Sample sales record:', salesRecords[0]);
        setSalesData(salesRecords)
      } else {
        console.log('No sales data found');
        setSalesData([])
      }
    })

    return () => {
      off(branchProfileRef)
      off(storesRef)
      off(customersRef)
      off(groupsRef)
      off(productsRef)
      off(salesRef)
    }
  }

  // Update sales data when productMaps change
  useEffect(() => {
    if (Object.keys(productGroupMap).length > 0 && currentUser) {
      setSalesData((prevData) =>
        prevData.map((item) => ({
          ...item,
          groupName: productGroupMap[item.itemDescription] || "No Group Assigned",
          category: productCategoryMap[item.itemDescription] || "Unknown Category",
          brand: productBrandMap[item.itemDescription] || "Unknown Brand",
        })),
      )
    }
  }, [productGroupMap, productCategoryMap, productBrandMap, currentUser])

  // Add a useEffect to update salesData with correct phoneNo and gstTinNo after customerMap is loaded
  useEffect(() => {
    setSalesData((prevSales) =>
      prevSales.map((item) => {
        const customerInfo = customerMap[item.customer] || {}
        return {
          ...item,
          customerPhone: customerInfo.phoneNo || "N/A",
          tin: customerInfo.gstTinNo || "N/A",
        }
      })
    )
  }, [customerMap])

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value })
  }

  const handleGenerateReport = () => {
    setShowReport(true)
  }

  const handlePrint = () => {
    const printContent = (
      <SalesReportGSTINDisplay
        formData={{ fromDate, toDate, searchBy, reportType }}
        filters={filters}
        branchProfile={branchProfile}
        salesData={salesData}
      />
    );
  
    const htmlString = ReactDOMServer.renderToString(printContent);
  
    const printWindow = window.open('', '', 'width=1200,height=800');
    printWindow.document.write(`
      <html>
        <head>
          <title>Sales Customerwise GSTIN Report</title>
          <style>
            body { 
              margin: 0; 
              padding: 5px; 
              font-family: Arial, sans-serif; 
              font-size: 8px;
            }
            @page { 
              size: A4 landscape; 
              margin: 5mm; 
            }
            table { 
              page-break-inside: auto;
              width: 100%;
              font-size: 7px;
              table-layout: fixed;
              border-collapse: collapse;
            }
            tr { 
              page-break-inside: avoid; 
              page-break-after: auto; 
            }
            th, td {
              padding: 2px !important;
              font-size: 7px !important;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 60px;
            }
            /* Specific column widths for abstract report */
            th:nth-child(1), td:nth-child(1) { width: 30px; } /* S.No */
            th:nth-child(2), td:nth-child(2) { width: 80px; } /* Customer Name */
            th:nth-child(3), td:nth-child(3) { width: 50px; } /* Phone Number */
            th:nth-child(4), td:nth-child(4) { width: 60px; } /* GSTIN */
            th:nth-child(5), td:nth-child(5) { width: 50px; } /* Inv.No */
            th:nth-child(6), td:nth-child(6) { width: 50px; } /* Inv.Date */
            th:nth-child(7), td:nth-child(7) { width: 50px; } /* Tax Amount (0.00%) */
            th:nth-child(8), td:nth-child(8) { width: 50px; } /* Taxable Value (0.00%) */
            th:nth-child(9), td:nth-child(9) { width: 50px; } /* Tax Amount (28.00%) */
            th:nth-child(10), td:nth-child(10) { width: 50px; } /* Taxable Value (28.00%) */
            th:nth-child(11), td:nth-child(11) { width: 50px; } /* Tax Amount (18.00%) */
            th:nth-child(12), td:nth-child(12) { width: 50px; } /* Taxable Value (18.00%) */
            th:nth-child(13), td:nth-child(13) { width: 50px; } /* GST Amnt */
            th:nth-child(14), td:nth-child(14) { width: 50px; } /* Taxable value (Cess 0%) */
            th:nth-child(15), td:nth-child(15) { width: 45px; } /* Cess (CESS 0%) */
            th:nth-child(16), td:nth-child(16) { width: 50px; } /* Item Disc Amt */
            th:nth-child(17), td:nth-child(17) { width: 50px; } /* Gross Amount */
            th:nth-child(18), td:nth-child(18) { width: 50px; } /* GST Amt */
            th:nth-child(19), td:nth-child(19) { width: 50px; } /* Bill Disc Amt */
            th:nth-child(20), td:nth-child(20) { width: 60px; font-weight: bold; } /* NetAmount */
            /* Specific column widths for detail report */
            ${formData.reportType === "detail" ? `
              th:nth-child(1), td:nth-child(1) { width: 30px; } /* S.No */
              th:nth-child(2), td:nth-child(2) { width: 80px; } /* Customer Name */
              th:nth-child(3), td:nth-child(3) { width: 50px; } /* Phone Number */
              th:nth-child(4), td:nth-child(4) { width: 60px; } /* TIN */
              th:nth-child(5), td:nth-child(5) { width: 50px; } /* Inv.No */
              th:nth-child(6), td:nth-child(6) { width: 50px; } /* Inv.Date */
              th:nth-child(7), td:nth-child(7) { width: 40px; } /* CGST % */
              th:nth-child(8), td:nth-child(8) { width: 40px; } /* SGST % */
              th:nth-child(9), td:nth-child(9) { width: 40px; } /* IGST % */
              th:nth-child(10), td:nth-child(10) { width: 50px; } /* GST Amt */
              th:nth-child(11), td:nth-child(11) { width: 40px; } /* CESS % */
              th:nth-child(12), td:nth-child(12) { width: 40px; } /* Disc % */
              th:nth-child(13), td:nth-child(13) { width: 50px; } /* Gross Amount */
              th:nth-child(14), td:nth-child(14) { width: 60px; font-weight: bold; } /* Net Amount */
            ` : ''}
            .print-only {
              width: 93% !important;
              max-width: none !important;
              overflow: visible !important;
            }
            div[style*="overflowX"] {
              overflow: visible !important;
            }
          </style>
        </head>
        <body>
          <div id="print-content">${htmlString}</div>
          <script>
            setTimeout(function() {
              window.print();
              window.close();
            }, 1000);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const formData = {
    fromDate,
    toDate,
    searchBy,
    reportType,
  }

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          fontSize: "18px",
          color: "#64748b",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #e2e8f0",
              borderTop: "4px solid #3b82f6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          ></div>
          <div>Loading Sales Data...</div>
        </div>
      </div>
    )
  }

  if (!currentUser) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "3rem",
          color: "#64748b",
          fontSize: "18px",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <h2 style={{ color: "#1e293b", marginBottom: "16px" }}>Authentication Required</h2>
        <p>Please login to access the Sales Customerwise GSTIN Report Generator</p>
      </div>
    )
  }

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "90rem",
        backgroundColor: "white",
        borderRadius: "16px",
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        padding: "32px",
        margin: "0 auto",
        fontFamily: "system-ui, -apple-system, sans-serif",
        border: "1px solid #e2e8f0",
      }}
    >
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "24px",
          paddingBottom: "16px",
          borderBottom: "2px solid #e2e8f0",
        }}
      >
        <div
          style={{
            width: "6px",
            height: "32px",
            backgroundColor: "#3b82f6",
            borderRadius: "3px",
          }}
        ></div>
        <h1
          style={{
            fontSize: "28px",
            fontWeight: "700",
            color: "#1e293b",
            margin: "0",
          }}
        >
          Customerwise (GSTIN)_1A
        </h1>
      </div>

      {/* Date Range and Search Filters */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "20px",
          marginBottom: "32px",
          padding: "24px",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            From Date
          </label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              backgroundColor: "white",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
          />
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            To Date
          </label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              backgroundColor: "white",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
          />
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            Search By
          </label>
          <select
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              backgroundColor: "white",
              cursor: "pointer",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
          >
            <option value="Customerwise">Customerwise</option>
          </select>
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            Report Type
          </label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              backgroundColor: "white",
              cursor: "pointer",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
          >
            <option value="detail">Detail</option>
            {/* <option value="abstract">Abstract</option> */}
          </select>
        </div>
      </div>

      {/* Filter Checkboxes */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
          marginBottom: "32px",
        }}
      >
        {/* <FilterSection
          title="Stores"
          options={stores}
          selected={filters.store}
          onChange={(value) => handleFilterChange("store", value)}
        /> */}
        <FilterSection
          title="Customer"
          options={customers}
          selected={filters.customer}
          onChange={(value) => handleFilterChange("customer", value)}
        />
        <FilterSection
          title="Group Name"
          options={groups}
          selected={filters.group}
          onChange={(value) => handleFilterChange("group", value)}
        />
        <FilterSection
          title="Product"
          options={products}
          selected={filters.product}
          onChange={(value) => handleFilterChange("product", value)}
        />
      </div>

      {/* Action Buttons */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "32px",
          justifyContent: "center",
        }}
      >
        <button
          onClick={handleGenerateReport}
          style={{
            background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
            color: "white",
            padding: "12px 24px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            transition: "all 0.3s",
            fontSize: "16px",
            fontWeight: "600",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "140px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)"
            e.currentTarget.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.15)"
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)"
            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)"
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
            color: "white",
            padding: "12px 24px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            transition: "all 0.3s",
            fontSize: "16px",
            fontWeight: "600",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "120px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)"
            e.currentTarget.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.15)"
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)"
            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)"
          }}
        >
          Print Report
        </button>
      </div>

      {/* Report Display */}
      {showReport && (
        <SalesReportGSTINDisplay
          formData={formData}
          filters={filters}
          branchProfile={branchProfile}
          salesData={salesData}
        />
      )}
    </div>
  )
}

export default SalesReportGSTIN_1A
